import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getHostels, getHostelAdmissions, getAttendanceRoster, markAttendance, bulkMarkAttendance, getAttendanceHistory,
  unmarkAttendance, bulkUnmarkAttendance,
  getHostelAdmissionOptions, getMealsHistory, getHostelPayments, getInvoices, getInvoice,
  getBillingAdjustments, addBillingAdjustment, deleteBillingAdjustment, getResidentHistoryByCnic,
} from '../api';
import { FiCheckSquare, FiCheck, FiX, FiUsers, FiClock, FiDownload, FiUser } from 'react-icons/fi';
import { GROUP_OPTIONS, groupRows } from '../utils/grouping';
import ResidentProfileModal from './ResidentProfileModal';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function downloadCsv(filename, rows) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Attendance() {
  const [searchParams] = useSearchParams();
  const urlHostelId = searchParams.get('hostel_id');
  const urlAdmissionId = searchParams.get('admission_id');

  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [roster, setRoster] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);

  const [admissions, setAdmissions] = useState([]);
  const [historyAdmissionId, setHistoryAdmissionId] = useState(urlAdmissionId || '');
  const [historyStart, setHistoryStart] = useState(firstOfMonth());
  const [historyEnd, setHistoryEnd] = useState(todayStr());
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [profileRecord, setProfileRecord] = useState(null);

  useEffect(() => {
    getHostels().then(res => {
      setHostels(res.data);
      if (urlHostelId) setHostelId(urlHostelId);
      else if (res.data.length && !hostelId) setHostelId(res.data[0].id);
    }).catch(() => setError('Failed to load hostels'));
    // Campus/course names are pure lookup data for grouping — fetched via the
    // unguarded meta/options endpoint so this works even for roles that
    // don't have the Campuses/Courses management modules granted.
    getHostelAdmissionOptions().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hostelId) return;
    getHostelAdmissions({ hostel_id: hostelId, status: 'active' }).then(res => {
      setAdmissions(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [hostelId]);

  // Picking a specific resident defaults the history range back to their own
  // enrollment date, so their complete attendance record is visible by
  // default instead of only "this month". Switching back to "All residents"
  // returns to the lighter this-month default.
  useEffect(() => {
    if (!historyAdmissionId) return;
    const adm = admissions.find(a => String(a.id) === String(historyAdmissionId));
    if (adm?.date_of_admission) setHistoryStart(adm.date_of_admission.slice(0, 10));
  }, [historyAdmissionId, admissions]);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const campusNameById = useMemo(() => Object.fromEntries(campuses.map(c => [c.id, c.name])), [campuses]);
  const courseNameById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c.name])), [courses]);

  const load = useCallback(async () => {
    if (!hostelId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getAttendanceRoster({ hostel_id: hostelId, date });
      setRoster(res.data.roster);
      setEnabled(res.data.attendance_enabled);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roster');
    }
    setLoading(false);
  }, [hostelId, date]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    if (!hostelId) return;
    setHistoryLoading(true);
    try {
      const params = { hostel_id: hostelId, start: historyStart, end: historyEnd };
      if (historyAdmissionId) params.admission_id = historyAdmissionId;
      const res = await getAttendanceHistory(params);
      setHistory(res.data);
    } catch (e) { /* non-fatal */ }
    setHistoryLoading(false);
  }, [hostelId, historyAdmissionId, historyStart, historyEnd]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setStatus = async (admissionId, status) => {
    const row = roster.find(r => r.admission_id === admissionId);
    const isEdit = row && row.attendance_status && row.attendance_status !== status;
    if (isEdit && !window.confirm(`${row.full_name} is currently marked ${row.attendance_status} for ${date}. Change this to ${status}?`)) return;
    setRoster(prev => prev.map(r => r.admission_id === admissionId ? { ...r, attendance_status: status } : r));
    try {
      await markAttendance({ admission_id: admissionId, attendance_date: date, status });
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      load();
    }
  };

  const unmarkOne = async (row) => {
    if (!window.confirm(`Unmark ${row.full_name}'s attendance for ${date}? This clears it back to "not marked".`)) return;
    setRoster(prev => prev.map(r => r.admission_id === row.admission_id ? { ...r, attendance_status: null } : r));
    try {
      await unmarkAttendance({ admission_id: row.admission_id, attendance_date: date });
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to unmark');
      load();
    }
  };

  const clearAll = async () => {
    const markedCount = roster.filter(r => r.attendance_status).length;
    if (!markedCount) return;
    if (!window.confirm(`Clear attendance for all ${markedCount} marked resident(s) on ${date}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await bulkUnmarkAttendance({ hostel_id: hostelId, attendance_date: date });
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to clear');
    }
    setSaving(false);
  };

  const editHistoryRow = async (row, status) => {
    try {
      await markAttendance({ admission_id: row.admission_id, attendance_date: row.attendance_date.slice(0, 10), status });
      loadHistory();
      if (row.attendance_date.slice(0, 10) === date) load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update');
    }
  };

  const markAllPresent = async () => {
    if (!window.confirm(`Mark ALL ${roster.length} resident(s) present for ${date}? This will overwrite any existing marks for today.`)) return;
    setSaving(true);
    try {
      const entries = roster.map(r => ({ admission_id: r.admission_id, status: 'present' }));
      await bulkMarkAttendance({ hostel_id: hostelId, attendance_date: date, entries });
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to bulk mark');
    }
    setSaving(false);
  };

  const exportHistoryCsv = () => {
    const rows = [['Date', 'Resident', 'Status', 'Marked By']];
    history.forEach(h => rows.push([new Date(h.attendance_date).toLocaleDateString('en-PK'), h.full_name, h.status, h.marked_by_name || h.marked_by || '']));
    downloadCsv(`attendance-${hostels.find(h => String(h.id) === String(hostelId))?.name || hostelId}-${historyStart}_to_${historyEnd}.csv`, rows);
  };

  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  const presentCount = roster.filter(r => r.attendance_status === 'present').length;
  const absentCount = roster.filter(r => r.attendance_status === 'absent').length;

  const rosterGroups = groupRows(roster, groupBy, admissionsById, campusNameById, courseNameById);
  const historyGroups = groupRows(history, groupBy, admissionsById, campusNameById, courseNameById);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiCheckSquare /> Daily Attendance</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Mark and review daily presence for hostel residents</p>
        </div>
      </div>

      <div className="form-row" style={{ marginBottom: '18px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Hostel</label>
          <select className="form-control" value={hostelId} onChange={e => setHostelId(e.target.value)}>
            {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Date</label>
          <input type="date" className="form-control" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Organize By</label>
          <select className="form-control" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!enabled && !loading && (
        <div className="alert alert-warning">
          Attendance tracking is not enabled for this hostel. Enable it from <strong>Billing Config</strong> for this hostel.
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : enabled && (
        <>
          <div className="stats-grid" style={{ marginBottom: '18px' }}>
            <div className="stat-card stat-purple">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-content"><div className="stat-value">{roster.length}</div><div className="stat-label">Total Residents</div></div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon"><FiCheck /></div>
              <div className="stat-content"><div className="stat-value">{presentCount}</div><div className="stat-label">Present Today</div></div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon"><FiX /></div>
              <div className="stat-content"><div className="stat-value">{absentCount}</div><div className="stat-label">Absent Today</div></div>
            </div>
          </div>

          <div className="action-buttons" style={{ marginBottom: '14px' }}>
            <button className="btn btn-dark btn-small" onClick={markAllPresent} disabled={saving || !roster.length}>
              {saving ? 'Saving...' : 'Mark All Present'}
            </button>
            <button className="btn btn-secondary btn-small" onClick={clearAll} disabled={saving || (!presentCount && !absentCount)}>
              Clear All for {date}
            </button>
          </div>

          {rosterGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: '18px' }}>
              {group.label && <h4 style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Resident</th><th>CNIC</th><th>Status</th><th>Mark</th></tr>
                  </thead>
                  <tbody>
                    {group.rows.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>No active residents in this hostel.</td></tr>
                    ) : group.rows.map((r, i) => (
                      <tr key={r.admission_id}>
                        <td>{i + 1}</td>
                        <td><strong>{r.full_name}</strong></td>
                        <td>{r.cnic || '—'}</td>
                        <td>
                          {r.attendance_status === 'present' && <span className="badge badge-success">Present</span>}
                          {r.attendance_status === 'absent' && <span className="badge badge-danger">Absent</span>}
                          {!r.attendance_status && <span className="badge badge-muted">Not marked</span>}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(r.admission_id, r.full_name)}><FiUser /></button>
                            <button className={`btn btn-small ${r.attendance_status === 'present' ? 'btn-success' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'present')}>Present</button>
                            <button className={`btn btn-small ${r.attendance_status === 'absent' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'absent')}>Absent</button>
                            {r.attendance_status && (
                              <button className="btn btn-small btn-secondary" title="Clear back to not marked" onClick={() => unmarkOne(r)}>Unmark</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {/* History */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FiClock /> Attendance History</h3>
          <button className="btn btn-secondary btn-small" onClick={exportHistoryCsv} disabled={!history.length}><FiDownload /> Export CSV</button>
        </div>
        <div className="form-row" style={{ marginBottom: '14px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Resident</label>
            <select className="form-control" value={historyAdmissionId} onChange={e => setHistoryAdmissionId(e.target.value)}>
              <option value="">All residents</option>
              {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>From{historyAdmissionId ? ' (defaults to enrollment date)' : ''}</label>
            <input type="date" className="form-control" value={historyStart} onChange={e => setHistoryStart(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="form-control" value={historyEnd} max={todayStr()} onChange={e => setHistoryEnd(e.target.value)} />
          </div>
        </div>

        {historyLoading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : historyGroups.map((group, gi) => (
          <div key={gi} style={{ marginBottom: '18px' }}>
            {group.label && <h4 style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Resident</th><th>Status</th><th>Marked By</th><th>Edit</th></tr></thead>
                <tbody>
                  {group.rows.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>No records in this range.</td></tr>
                  ) : group.rows.map(row => (
                    <tr key={row.id}>
                      <td>{new Date(row.attendance_date).toLocaleDateString('en-PK')}</td>
                      <td>{row.full_name}</td>
                      <td>{row.status === 'present' ? <span className="badge badge-success">Present</span> : <span className="badge badge-danger">Absent</span>}</td>
                      <td>{row.marked_by_name || row.marked_by || '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-secondary btn-small" title="View resident profile" onClick={() => viewProfile(row.admission_id, row.full_name)}><FiUser /></button>
                          <button className="btn btn-secondary btn-small" onClick={() => editHistoryRow(row, row.status === 'present' ? 'absent' : 'present')}>
                            Switch to {row.status === 'present' ? 'Absent' : 'Present'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistory({ admission_id: id })}
          fetchMeals={(id) => getMealsHistory({ admission_id: id })}
          fetchPayments={(id) => getHostelPayments({ admission_id: id })}
          fetchInvoices={(id) => getInvoices({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoice(id)}
          fetchAdjustments={(id) => getBillingAdjustments({ admission_id: id })}
          fetchResidentHistory={(cnic) => getResidentHistoryByCnic(cnic)}
          canManageAdjustments
          onAddAdjustment={addBillingAdjustment}
          onDeleteAdjustment={deleteBillingAdjustment}
        />
      )}
    </div>
  );
}
